import { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui';
import scheduleClearBodyPointerEventsLocks from './helpers/scheduleClearBodyPointerEventsLocks';
import type { AlertDialogOptions } from './types';

interface UseAlertDialogReturn {
    confirm: (options: AlertDialogOptions) => Promise<boolean>;
}

const useAlertDialog = (): UseAlertDialogReturn => {
    const confirm = useCallback((options: AlertDialogOptions): Promise<boolean> => {
        return new Promise(resolve => {
            const container = document.createElement('div');
            document.body.appendChild(container);
            const root = createRoot(container);

            let settled = false;
            const settle = (result: boolean) => {
                if (settled) return;
                settled = true;
                root.unmount();
                if (container.parentNode === document.body) {
                    document.body.removeChild(container);
                }
                // Abrupt createRoot unmount races Radix remove-scroll cleanup,
                // especially after DropdownMenu → AlertDialog (overflow revoke).
                scheduleClearBodyPointerEventsLocks({ reason: 'alert-dialog-settle' });
                resolve(result);
            };

            const handleConfirm = () => {
                settle(true);
            };

            const handleCancel = () => {
                settle(false);
            };

            const AlertDialogComponent = () => {
                const [open, setOpen] = useState(true);

                const onOpenChange = (isOpen: boolean) => {
                    if (!isOpen) {
                        handleCancel();
                    }
                    setOpen(isOpen);
                };

                return (
                    <AlertDialog open={open} onOpenChange={onOpenChange}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                {options.title && (
                                    <AlertDialogTitle>{options.title}</AlertDialogTitle>
                                )}
                                {options.description && (
                                    <AlertDialogDescription>
                                        {options.description}
                                    </AlertDialogDescription>
                                )}
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel onClick={handleCancel}>
                                    {options.cancelLabel || 'Cancel'}
                                </AlertDialogCancel>
                                <AlertDialogAction onClick={handleConfirm}>
                                    {options.confirmLabel || 'Continue'}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                );
            };

            root.render(<AlertDialogComponent />);
        });
    }, []);

    return {
        confirm,
    };
};

export default useAlertDialog;
